import type { InvoiceDataId, ValidationOptions } from '@subscriptions/domain/types';
import type { PostalCode, Rfc, CfdiUse, Email, TaxRegime } from '@subscriptions/domain/value-objects';
import {
  createPostalCode,
  createRfc,
  createCfdiUse,
  createEmail,
  createTaxRegime,
} from '@subscriptions/domain/value-objects';
import { createValueResolver } from '@subscriptions/domain/utils';

/**
 * Invoice data (datos fiscales) domain entity.
 *
 * Immutable by design — represents fiscal information for billing.
 * Only created/reconstructed via factory function.
 */
export type InvoiceData = Readonly<{
  uuid?: InvoiceDataId;
  orgUuid?: string;
  businessName: string;
  firstName: string;
  firstLastName: string;
  secondLastName: string;
  postalCode: PostalCode;
  rfc: Rfc;
  cfdi: CfdiUse;
  email: Email;
  taxRegime: TaxRegime;
}>;

/**
 * Creates a validated InvoiceData entity.
 *
 * @param params - Raw primitives from application or infrastructure layer
 * @param options - Validation options; with `softValidation` invalid fields degrade to empty fallbacks
 * @returns Frozen InvoiceData entity
 * @throws Error if any required field is invalid (strict mode only)
 */
export const createInvoiceData = (
  params: {
    uuid?: string;
    orgUuid?: string;
    businessName: string;
    firstName: string;
    firstLastName: string;
    secondLastName: string;
    postalCode: string;
    rfc: string;
    cfdi: string;
    email: string;
    taxRegime: string;
  },
  options?: ValidationOptions,
): InvoiceData => {
  const resolve = createValueResolver(options);

  return Object.freeze({
    uuid: params.uuid ? (params.uuid as InvoiceDataId) : undefined,
    orgUuid: params.orgUuid,
    businessName: params.businessName,
    firstName: params.firstName,
    firstLastName: params.firstLastName,
    secondLastName: params.secondLastName,
    postalCode: resolve<PostalCode>(() => createPostalCode(params.postalCode), ''),
    rfc: resolve<Rfc>(() => createRfc(params.rfc), ''),
    cfdi: resolve<CfdiUse>(() => createCfdiUse(params.cfdi), ''),
    email: resolve<Email>(() => createEmail(params.email), ''),
    taxRegime: resolve<TaxRegime>(() => createTaxRegime(params.taxRegime), ''),
  });
};
